import { money } from './shared.jsx';

export const SIMPLE_CALCULATION_OPTIONS = [
  { value: 'fixed', label: 'Fijo', unidadBase: 'GUIA', metodoCalculo: 'FIJO' },
  { value: 'per_kg', label: 'Por kilo', unidadBase: 'KG', metodoCalculo: 'POR_UNIDAD' },
  { value: 'per_package', label: 'Por bulto', unidadBase: 'BULTO', metodoCalculo: 'POR_UNIDAD' },
  { value: 'percentage', label: 'Porcentaje', unidadBase: 'VALOR_DECLARADO', metodoCalculo: 'PORCENTAJE' },
];

export function inferSimpleCalculationType(rule = {}) {
  if (rule.metodoCalculo === 'PORCENTAJE') return 'percentage';
  if (rule.metodoCalculo === 'POR_UNIDAD' && rule.unidadBase === 'KG') return 'per_kg';
  if (rule.metodoCalculo === 'POR_UNIDAD' && rule.unidadBase === 'BULTO') return 'per_package';
  return 'fixed';
}

export function getSimpleCalculationConfig(type) {
  return SIMPLE_CALCULATION_OPTIONS.find((item) => item.value === type) || SIMPLE_CALCULATION_OPTIONS[0];
}

export function suggestSimpleCalculationType(concept = {}) {
  const haystack = `${concept?.codigo || ''} ${concept?.nombre || ''}`.toLowerCase();
  if (haystack.includes('flete') || haystack.includes('peso') || haystack.includes('kg')) return 'per_kg';
  if (haystack.includes('bulto') || haystack.includes('paquete')) return 'per_package';
  if (haystack.includes('seguro') || haystack.includes('declarado') || haystack.includes('contra')) return 'percentage';
  return 'fixed';
}

const UNIT_LABELS = { KG: 'kilo', BULTO: 'bulto', M3: 'm3', KILO_AFORADO: 'kilo aforado', GUIA: 'guía' };

// Code seed and label of the rule's own calculation, so an advanced rule (per m3, per aforo)
// is not named as if it were the closest simple type.
function describeCalculation(form = {}) {
  const simple = getSimpleCalculationConfig(inferSimpleCalculationType(form));
  if (form.metodoCalculo !== 'POR_UNIDAD' || !form.unidadBase || simple.unidadBase === form.unidadBase) {
    return { seed: simple.value.toUpperCase(), label: simple.label.toLowerCase() };
  }
  const unit = String(form.unidadBase).toUpperCase();
  return { seed: `PER_${unit}`, label: `por ${UNIT_LABELS[unit] || unit.toLowerCase().replace(/_/g, ' ')}` };
}

export function buildRuleCode({ concept, form }) {
  const conceptSeed = String(concept?.codigo || concept?.nombre || 'REGLA')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  const scopeSeed = [
    form?.clienteId ? `CLI${form.clienteId}` : '',
    form?.servicioCodigo ? String(form.servicioCodigo).toUpperCase() : '',
    form?.origenLocalidadId ? `ORG${form.origenLocalidadId}` : '',
    form?.destinoLocalidadId ? `DST${form.destinoLocalidadId}` : '',
  ].filter(Boolean).join('_');
  const calcSeed = describeCalculation(form).seed;
  return [conceptSeed || 'REGLA', calcSeed, scopeSeed].filter(Boolean).join('_');
}

export function buildRuleName({ concept, form, labels = {} }) {
  const baseName = concept?.nombre || concept?.codigo || 'Regla';
  const calcLabel = describeCalculation(form).label;
  const conditions = [];
  if (labels.cliente) conditions.push(`cliente ${labels.cliente}`);
  if (labels.servicio) conditions.push(`servicio ${labels.servicio}`);
  if (labels.origen) conditions.push(`origen ${labels.origen}`);
  if (labels.destino) conditions.push(`destino ${labels.destino}`);
  if (!conditions.length && form?.clienteId) conditions.push(`cliente ${form.clienteId}`);
  if (!conditions.length && form?.servicioCodigo) conditions.push(`servicio ${form.servicioCodigo}`);
  return `${baseName} ${calcLabel}${conditions.length ? ` ${conditions.join(' / ')}` : ''}`.trim();
}

export function resolveScopeLabel(rule = {}) {
  const hasCliente = Boolean(rule.clienteId);
  const hasServicio = Boolean(rule.servicioCodigo);
  const hasGeo = Boolean(rule.origenSucursalId || rule.destinoSucursalId || rule.origenLocalidadId || rule.destinoLocalidadId);
  const activeFilters = [hasCliente, hasServicio, hasGeo].filter(Boolean).length;

  if (!activeFilters) return 'General';
  if (hasCliente && activeFilters === 1) return 'Por cliente';
  if (hasServicio && activeFilters === 1) return 'Por servicio';
  return 'Combinado';
}

export function getSuggestedPriority(rule = {}) {
  const scope = resolveScopeLabel(rule);
  if (scope === 'Por cliente') return 300;
  if (scope === 'Por servicio') return 200;
  if (scope === 'Combinado') return 400;
  return 100;
}

export function buildRuleSummary({ rule = {}, conceptLabel = '', labels = {} }) {
  const subject = conceptLabel || 'La regla';
  const value = rule.metodoCalculo === 'PORCENTAJE'
    ? `${Number(rule.porcentaje || 0)}%`
    : money(rule.valorUnitario || rule.cantidadFija || 0);
  let calcText = `${value} fijo`;

  if (rule.metodoCalculo === 'POR_UNIDAD' && rule.unidadBase === 'KG') calcText = `${value} por kilo`;
  else if (rule.metodoCalculo === 'POR_UNIDAD' && rule.unidadBase === 'BULTO') calcText = `${value} por bulto`;
  else if (rule.metodoCalculo === 'POR_UNIDAD') calcText = `${value} por ${String(rule.unidadBase || 'unidad').toLowerCase()}`;
  else if (rule.metodoCalculo === 'PORCENTAJE') calcText = `${value} sobre ${String(rule.unidadBase || 'valor declarado').toLowerCase()}`;

  const conditions = [];
  if (labels.cliente) conditions.push(`cliente = ${labels.cliente}`);
  else if (rule.clienteId) conditions.push(`cliente = ${rule.clienteId}`);
  if (labels.servicio || rule.servicioCodigo) conditions.push(`servicio = ${labels.servicio || rule.servicioCodigo}`);
  if (labels.origen || rule.origenLocalidadId || rule.origenSucursalId) conditions.push(`origen = ${labels.origen || rule.origenLocalidadId || rule.origenSucursalId}`);
  if (labels.destino || rule.destinoLocalidadId || rule.destinoSucursalId) conditions.push(`destino = ${labels.destino || rule.destinoLocalidadId || rule.destinoSucursalId}`);

  return `${subject} = ${calcText}${conditions.length ? ` cuando ${conditions.join(' y ')}` : ''}`;
}

export function buildSimulationDraft(rule = {}, currentSimulation = {}) {
  const next = {
    ...currentSimulation,
    versionId: rule.versionId || currentSimulation.versionId || '',
    // The rule's list is remembered, but only a forced simulation restricts the valuation to it.
    forzarVersion: false,
    clienteId: rule.clienteId || currentSimulation.clienteId || '',
    subcuentaId: rule.subcuentaId || currentSimulation.subcuentaId || '',
    servicioCodigo: rule.servicioCodigo || currentSimulation.servicioCodigo || '',
    numeradorId: rule.numeradorId || currentSimulation.numeradorId || '',
    origenSucursalId: rule.origenSucursalId || currentSimulation.origenSucursalId || '',
    destinoSucursalId: rule.destinoSucursalId || currentSimulation.destinoSucursalId || '',
    origenLocalidadId: rule.origenLocalidadId || currentSimulation.origenLocalidadId || '',
    destinoLocalidadId: rule.destinoLocalidadId || currentSimulation.destinoLocalidadId || '',
    kg: currentSimulation.kg || 10,
    bultos: currentSimulation.bultos || 1,
    m3: currentSimulation.m3 || 0,
    kgAforado: currentSimulation.kgAforado || 0,
    valorDeclarado: currentSimulation.valorDeclarado || 0,
    contrareembolso: currentSimulation.contrareembolso || 0,
  };

  if (rule.unidadBase === 'KG') next.kg = next.kg || 10;
  if (rule.unidadBase === 'BULTO') next.bultos = next.bultos || 1;
  if (rule.unidadBase === 'M3') next.m3 = next.m3 || 1;
  if (rule.unidadBase === 'KILO_AFORADO') next.kgAforado = next.kgAforado || 10;
  if (rule.unidadBase === 'VALOR_DECLARADO') next.valorDeclarado = next.valorDeclarado || 100000;
  if (rule.unidadBase === 'CONTRAREEMBOLSO') next.contrareembolso = next.contrareembolso || 100000;

  return next;
}

// A normal simulation resolves lists and versions exactly like guide emission (dates, state and customer
// assignment). Sending versionId or listaId skips those filters, so they go only in a forced simulation.
export function buildSimulationPayload(simulationForm = {}) {
  const clienteId = Number(simulationForm?.clienteId);
  if (!Number.isInteger(clienteId) || clienteId <= 0) return null;
  const { forzarVersion, versionId, listaId, ...rest } = simulationForm;
  return {
    ...rest,
    clienteId,
    ...(forzarVersion && versionId ? { versionId } : {}),
    ...(forzarVersion && listaId ? { listaId } : {}),
  };
}
